import { yupResolver } from '@hookform/resolvers/yup';
import React, { useEffect, useState } from 'react';
import { Col, Form, Row } from 'react-bootstrap';
import { useFieldArray, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { v4 as uuidv4 } from 'uuid';
import * as Yup from 'yup';

import { useAppDispatch, useAppSelector } from '@/app/hook';
import { PSButton } from '@/components/ui/button';
import { PSInput } from '@/components/ui/input';
import { AlertsType } from '@/constants/alerts.constant';
import { selectUseId } from '@/features/auth/auth.slice';
import { resetUndoRedoScenario } from '@/features/scenario.slice';
import { BlockType } from '@/interfaces/models/Knowledge/scenario.interface';

import HintBlock from '../../../../common/HintBlock';
import AddNoteButton from '../../AddNoteButton';
import NodeParentChanger from '../../NodeParentChanger';
import NoteForm, { getNoteSchema, INoteForm } from '../../NoteForm';
import ScenarioHeader from '../../ScenarioHeader';
import { MacBotActionKind } from '../../types';
import { MacBotButtonSettingForm } from './scenario-button-setting-form.component';
import TextScenarioDeleteButton from './scenario-delete-button';
import ScenarioSide from './scenario-side';
import TextScenarioLinkTypeComponent from './text-scenario-link-type-component';

export const TextScenarioLinkForm = ({
  setShow,
  itemData,
  parentId,
  tree,
  loading,
  createScenarioBlock,
  deleteScenarioBlock,
  changeScenarioBlock,
}: {
  setShow?: (item: boolean) => void;
  itemData: any;
  parentId: string | null;
  tree: any[];
  loading: boolean;
  createScenarioBlock: (block: any) => Promise<void>;
  deleteScenarioBlock: (block: any, isUpdateTree: boolean, isUndoRedoState: boolean) => Promise<void>;
  changeScenarioBlock: (block: any, isUpdateTree: boolean, isUndoRedoState: boolean) => Promise<void>;
}) => {
  const { t } = useTranslation();
  const [showButtonSetting, setShowButtonSetting] = useState(false);
  const [selectedButtonToEdit, setSelectedButtonToEdit] = useState<number>(-1);
  const [quickReplies, setQuickReplies] = useState<any[]>([]);
  const dispatch: any = useAppDispatch();
  const userId = useAppSelector(selectUseId);

  const prefixLinkList = ['https://wa.me/', 'mailto:', 'tel:', 'https://'];

  interface IResponseForm extends INoteForm {
    BlockTitle?: string;
    BotResponse: string;
    Logo: string;
    Link: string;
    OrderId?: number;
    prefix: string;
  }

  const schema = Yup.object({
    BotResponse: Yup.string().required(t('MacBotLinkForm/t-01')),
    Link: Yup.string().required(t(AlertsType.ISKBKO030)),
    prefix: Yup.string()
      .oneOf(prefixLinkList, t('Invalid prefix')) // Validation rule for prefix
      .required(t('Prefix is required')),
    ...getNoteSchema(t),
  });

  const {
    register,
    formState: { errors },
    reset,
    watch,
    trigger,
    getValues,
    setValue,
    handleSubmit,
    control,
  } = useForm<IResponseForm>({
    defaultValues: {
      BlockTitle: itemData ? itemData.title : '',
      BotResponse: itemData ? itemData.message : '',
      Logo: itemData ? itemData?.logo : 'https://cdn.1cdn.app/application/LOUISEBOT/2409021316194_bx-link-external.svg',
      OrderId: itemData ? itemData.order_id : '',
      Link: itemData ? itemData.link : '',
      prefix: 'https://',
      notes: [
        {
          note_id: uuidv4(),
          creation_date_time: new Date().toISOString(),
          title: '',
          description: '',
          qc: false,
          author: userId,
        },
      ],
    },
    resetOptions: {
      keepDefaultValues: true,
    },
    resolver: yupResolver(schema) as any,
    mode: 'onSubmit',
  });
  const { fields } = useFieldArray({
    control,
    name: 'notes',
  });
  const [isDelete, setIsDelete] = useState(true);
  useEffect(() => {
    if (!itemData) {
      setIsDelete(false);
    }
  }, [itemData]);

  useEffect(() => {
    if (itemData && itemData?.title) setValue('BlockTitle', itemData?.title);
    if (itemData && itemData?.label) setValue('BotResponse', itemData?.label);
    if (itemData && itemData?.logo)
      setValue('Logo', itemData?.logo ? itemData?.logo : 'https://cdn.1cdn.app/application/LOUISEBOT/2409021316194_bx-link-external.svg');
    if (itemData) setValue('OrderId', getOrderIndexId(itemData));

    if (itemData && itemData?.order_id && itemData?.link) {
      const data = splitUrl(itemData?.link);

      if (data) {
        setValue('Link', data.remaining);
        setValue('prefix', data.prefix);
      }
    } else setQuickReplies([]);
    if (itemData) {
      setValue('notes', itemData?.notes);
    }
  }, [itemData]);

  const splitUrl = (url: string) => {
    for (const prefix of prefixLinkList) {
      if (url.startsWith(prefix)) {
        return {
          prefix: prefix,
          remaining: url.slice(prefix.length),
        };
      }
    }
    return null; // Return null if no prefix matches
  };

  const [placeHolder, setPlaceHolder] = useState('');

  const deleteHandler = () => {
    deleteScenarioBlock(itemData, true, true);
  };

  const removeButtonHandler = (btnIndex: number) => {
    setQuickReplies(prev => prev.filter((p, index) => index !== btnIndex));
  };
  const addButtonHandler = (qBtn: number) => {
    setQuickReplies(prev => [...prev, qBtn]);
  };

  const editButtonHandler = (_index: number, qRep: any) => {
    setQuickReplies(prev => prev.map((p, index) => (index === _index ? qRep : p)));

    setSelectedButtonToEdit(-1);
  };

  const closeButtonSettingModal = () => {
    setShowButtonSetting(false);
    setSelectedButtonToEdit(-1);
  };

  function delay(milliseconds: number) {
    return new Promise(resolve => setTimeout(resolve, milliseconds));
  }

  const getOrderIndexId = (itemData: any) => {
    return getOrderButtonsList().findIndex(dr => dr.order_id == itemData.order_id && dr.id == itemData.id) + 1;
  };

  const ButtonSubmit = (data: any) => {
    let orderId = null;

    if (data.OrderId && data.OrderId !== '') {
      const orderButtonsList = getOrderButtonsList();
      orderId = orderButtonsList[data.OrderId - 1].order_id;
    }

    let _data = {
      type: BlockType.TextLink,
      title: data.BotResponse,
      label: data.BotResponse,
      link: data.prefix.toLocaleLowerCase() + data.Link,
      logo: data.Logo == '' ? null : data.Logo,
      parent: itemData ? itemData.parent : parentId,
      order_id: itemData ? +orderId : getNewOrderButtons(),
      notes: data.notes,
    };

    if (itemData) {
      if (orderId != itemData.order_id) {
        const replaceNode = tree.find(dr => dr.parent == itemData.parent && dr.order_id == orderId);

        if (replaceNode) {
          dispatch(resetUndoRedoScenario());
          const updatedNode = { ...replaceNode, order_id: itemData.order_id };
          changeScenarioBlock(updatedNode, false, false).then(() => {
            delay(500).then(res => {
              _data = { ...itemData, ..._data, id: itemData.id };
              changeScenarioBlock(_data, true, false);
            });
          });
        }
      } else {
        delay(500).then(res => {
          _data = { ...itemData, ..._data, id: itemData.id };
          changeScenarioBlock(_data, true, false);
        });
      }
    } else {
      createScenarioBlock(_data);
    }
  };

  const getOrderButtonsList = (): any[] => {
    const parent = itemData ? itemData.parent : parentId;
    const filteredTree = tree.filter(dr => dr.parent == parent && (dr.type === BlockType.Button || dr.type === BlockType.TextLink));
    const sortedTree = filteredTree.sort((a, b) => a.order_id - b.order_id);
    return sortedTree;
  };

  const getNewOrderButtons = () => {
    const orderButtonsList = getOrderButtonsList();
    const node = orderButtonsList[orderButtonsList.length - 1];

    if (!node) return 1;

    for (let i = 1; i < 20; i++) {
      if (orderButtonsList.find(dr => dr.order_id != node.order_id + i)) {
        return node.order_id + i;
      }
    }
  };

  const onOrderChange = (event: any) => {
    setValue('OrderId', +event.target.value);
  };

  return (
    <React.Fragment>
      <ScenarioSide size="md" onSubmit={handleSubmit(ButtonSubmit)}>
        <ScenarioHeader
          type={MacBotActionKind.ShowLINKForm}
          onClose={() => {
            reset();
          }}
        />

        <div className="tw-p-3 tw-w-full">
          <Row className="gy-3">
            <Col xs={12}>
              <Row className="gx-2">
                <Col sm={2} xs={3}>
                  <Form.Group>
                    <Form.Label className="tw-text-[#687076] tw-text-xs tw-font-normal tw-font-['Lexend'] tw-leading-none">
                      {t('MacBotLinkForm/icon')}
                    </Form.Label>
                    <TextScenarioLinkTypeComponent
                      value={getValues('Logo') ? getValues('Logo') : 'https://cdn.1cdn.app/application/LOUISEBOT/2409021316194_bx-link-external.svg'}
                      onChange={({ url, prefix, placeHolder }: { url: string; prefix: string; placeHolder: string }) => {
                        if (url) {
                          setValue('Logo', url);
                          setValue('prefix', prefix);
                          setPlaceHolder(placeHolder);
                        } else {
                          setValue('Logo', 'https://cdn.1cdn.app/application/LOUISEBOT/2409021316194_bx-link-external.svg');
                        }
                      }}
                    />
                  </Form.Group>
                </Col>

                <Col>
                  <Form.Group>
                    <Form.Label className="tw-text-[#687076] tw-text-xs tw-font-normal tw-font-['Lexend'] tw-leading-none">
                      {t('MacBotLinkForm/text')}
                    </Form.Label>
                    <Form.Control
                      className="tw-self-stretch tw-px-4 tw-py-3 tw-bg-white tw-rounded-md tw-shadow tw-border tw-border-gray-300 placeholder:tw-text-gray-400"
                      maxLength={20}
                      placeholder={t('MacBotLinkForm/l-04')}
                      {...register('BotResponse')}
                      onFocus={e => (e.target.placeholder = '')}
                      onBlur={e => (e.target.placeholder = t('MacBotLinkForm/l-04'))}
                    />
                    <HintBlock texts={['MacBotLinkForm/maxlenght']} />
                    <Form.Control.Feedback type="invalid" className="d-block font-poppins">
                      {errors.BotResponse?.message}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
              </Row>
            </Col>

            <Col xs={12}>
              <div className="tw-h-[0px] tw-border tw-border-solid tw-border-gray-200"></div>
            </Col>

            <Col md="12">
              <Form.Group>
                <Form.Label className="tw-text-[#687076] tw-text-xs tw-font-normal tw-font-['Lexend'] tw-leading-none">{t('MacBotLinkForm/l-07')}</Form.Label>
                <div className="tw-h-11 tw-w-full tw-rounded-md tw-shadow tw-border tw-border-solid tw-border-gray-300 tw-justify-start tw-items-center tw-inline-flex">
                  <div className="tw-self-stretch tw-pl-4 tw-pr-3 tw-py-3 tw-bg-gray-50 tw-rounded-tl-md tw-rounded-bl-md tw-border tw-border-t-0 tw-border-l-0 tw-border-b-0 tw-border-solid tw-border-gray-300 tw-justify-start tw-items-center tw-flex">
                    <div className="tw-text-gray-500 tw-text-sm tw-font-normal tw-font-['Lexend'] tw-leading-tight">{watch('prefix') ?? 'https://'}</div>
                  </div>
                  <div className="tw-grow tw-shrink tw-basis-0 tw-self-stretch tw-pl-3 tw-pr-[13px] tw-py-3 tw-bg-white tw-rounded-tr-md tw-rounded-br-md tw-justify-start tw-items-center tw-flex">
                    <PSInput
                      className="tw-w-full tw-border-0 tw-outline-none"
                      {...register('Link')}
                      placeholder={placeHolder}
                      onFocus={e => (e.target.placeholder = '')}
                      onBlur={e => (e.target.placeholder = placeHolder)}
                    />
                  </div>
                </div>
                <Form.Control.Feedback type="invalid" className="d-block font-poppins">
                  {errors.Link?.message}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>

            <Col xs={12}>
              <div className="tw-h-[0px] tw-border tw-border-solid tw-border-gray-200"></div>
            </Col>

            {itemData && (
              <Form.Group className="tw-w-max">
                <Form.Label className="tw-text-[#687076] tw-text-xs tw-font-normal tw-font-['Lexend'] tw-leading-none">{t('MacBotLinkForm/l-06')}</Form.Label>
                <Form.Select translate="no" className="color-fifthColor" aria-label={t('MacBotGotoForm/l-04')} {...register('OrderId')}>
                  {getOrderButtonsList()?.map((dr, index) => (
                    <option key={dr.id}>{index + 1}</option>
                  ))}
                </Form.Select>
              </Form.Group>
            )}
          </Row>
        </div>

        <div className="tw-flex flex-row tw-justify-between tw-items-center">
          <div className="tw-flex tw-justify-start tw-items-center tw-gap-2">
            <TextScenarioDeleteButton itemData={itemData} tree={tree} deleteHandler={deleteHandler} />

            {itemData?.id && <NodeParentChanger tree={tree} currentId={itemData.id} />}
            <AddNoteButton noteFormMethods={{ getValues, setValue }} />
          </div>
          <div className="tw-flex tw-justify-end">
            <PSButton id="btn-link-submit" type="submit" disabled={loading} isLoading={loading} title={t('MacBotResponseForm/btn-02')} />
          </div>
        </div>
        <NoteForm noteFormMethods={{ register, watch, errors, fields, setValue }} />
      </ScenarioSide>

      <MacBotButtonSettingForm
        show={showButtonSetting}
        closeModal={closeButtonSettingModal}
        addQuickReplies={addButtonHandler}
        editQuickReplies={editButtonHandler}
        selectedButtonToEdit={selectedButtonToEdit}
        quickReplies={quickReplies}
        removeButton={removeButtonHandler}
        tree={tree}
      />
    </React.Fragment>
  );
};
